import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getProspects,
  getGradYears,
  requestProspectExport,
  toggleFavorite,
  updateProspectTags,
  updatePositionPlayerMetric,
  updatePitcherMetric,
  updateProspectProfile,
  getDefaultTagDefinitions,
  bulkDeleteProspects,
  getAllProspectsForTeam,
  reorderProspects,
} from "../../../../services/prospects";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { updateProspectTeam, deleteTeam } from "../../../../services/teams";
import type { UpdateProspectProfileData } from "../../../../services/prospects";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { pinFavoritesFirst } from "../../../../utils/sortProspects";
import type { ProspectSortColumn, TableSort } from "../../../../types/sort";
import {
  DEFAULT_TABLE_SORT,
  nextTableSort,
  parseTableSort,
} from "../../../../types/sort";
import {
  getStoredTeamChipIds,
  saveTeamChipIds,
  clearTeamChipIds,
  rehydrateTeamChips,
} from "../../../../utils/teamChipsStorage";
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  SEARCH_DEBOUNCE_MS,
} from "../../../../constants/extraction";
import {
  getStoredPageSize,
  savePageSize,
} from "../../../../utils/pageSizeStorage";
import {
  getStoredColumnLayout,
  saveColumnLayout,
} from "../../../../utils/playersTableColumnStorage";
import {
  EMPTY_COLUMN_LAYOUT,
  isSortedColumnHidden,
  type PlayersColumnLayout,
} from "./playersTableColumns";
import { getTeams } from "../../../../services/teams";
import { useAuth } from "../../../../hooks/useAuth";
import type {
  ProspectResponse,
  TeamResponse,
  BulkAddProspectsToTeamResponse,
} from "../../../../types/api";
import type { TagFilters, DefaultTagDefinition } from "../../../../types/tags";
import { countActiveFilters, pruneJucoYearFacet } from "../../../../types/tags";
import { ErrorBoundary } from "../../../../components/ErrorBoundary";
import { PlayersToolbar } from "./PlayersToolbar";
import { TeamChips } from "./TeamChips";
import { jucoTeamIds } from "../../../../utils/juco-team-rollup";
import { PlayersTable } from "./PlayersTable";
import { useTour } from "../../../tours/useTour";
import { TablePagination } from "../../../../components/TablePagination";
import { ProspectViewerPanel } from "./ProspectViewerPanel";
import { AddProspectPanel } from "./AddProspectPanel";
import { AddTeamPanel } from "./AddTeamPanel";
import { ManageTeamsPanel } from "./ManageTeamsPanel";
import { ProspectExportsPanel } from "./ProspectExportsPanel";
import { RosterUploadModal } from "./RosterUploadModal";
import { TagFilterModal } from "./TagFilterModal";
import { TagDefinitionManagerModal } from "./TagDefinitionManagerModal";
import { AddPlayersToTeamModal } from "./AddPlayersToTeamModal";
import { MergeProspectsModal } from "./merge/MergeProspectsModal";
import { TagSelectorModal } from "../../../../components/tags/TagSelectorModal";
import { GetStartedPanel } from "../../../onboarding/GetStartedPanel";
import "./PlayersSection.css";

// Grad Year filter options: a fixed, plausible window around the current year
// (currentYear-6 .. currentYear+8). Real grad years from the API are included
// only if they fall inside this window, which keeps bad data (e.g. 2076) out of
// the filter list.
const GRAD_YEAR_SPAN_BACK = 6;
const GRAD_YEAR_SPAN_FORWARD = 8;
const buildGradYearOptions = (fromData: number[] = []): number[] => {
  const now = new Date().getFullYear();
  const min = now - GRAD_YEAR_SPAN_BACK;
  const max = now + GRAD_YEAR_SPAN_FORWARD;
  const years = new Set<number>();
  for (let y = min; y <= max; y++) {
    years.add(y);
  }
  // Only surface real grad years within the plausible window — implausible
  // values in the data (e.g. 2076) are excluded from the filter options.
  for (const y of fromData) {
    if (y >= min && y <= max) {
      years.add(y);
    }
  }
  return Array.from(years).sort((a, b) => b - a);
};

/**
 * Serialize tag filters to URL search params
 */
const serializeTagFilters = (filters: TagFilters): string => {
  if (Object.keys(filters).length === 0) return "";
  return JSON.stringify(filters);
};

/**
 * Deserialize tag filters from URL search params
 *
 * The reserved scalar facets ride in the same JSON blob as the tag categories, so Level
 * and JC Year round-trip through the URL for free. What does not come for free is their
 * interaction: a bookmarked or hand-edited URL can pair a JC Year with a Level that no
 * longer includes JUCO, and since the server ANDs across facets that combination returns
 * nothing at all. Prune it on the way in, so the restored state matches what the filter
 * modal would let a coach build.
 */
const deserializeTagFilters = (value: string | null): TagFilters => {
  if (!value) return {};
  try {
    return pruneJucoYearFacet(JSON.parse(value));
  } catch {
    return {};
  }
};

type ProspectSection = "profile" | "videos" | "notes" | "about";

const PROSPECT_SECTIONS: readonly ProspectSection[] = [
  "profile",
  "videos",
  "notes",
  "about",
];

const parseSectionParam = (value: string | null): ProspectSection | null => {
  if (!value) return null;
  // Backwards compat for URLs created before the Docs → Notes rename. Bookmarks
  // and stale Latest Notes navigations keep working for one release.
  const normalized = value === "docs" ? "notes" : value;
  return (PROSPECT_SECTIONS as readonly string[]).includes(normalized)
    ? (normalized as ProspectSection)
    : null;
};

/**
 * Initialize state from URL search params
 */
const getInitialStateFromUrl = (searchParams: URLSearchParams) => {
  const search = searchParams.get("search") || "";
  const favoritesActive = searchParams.get("favorites") === "true";
  // The "favorites" sentinel shares the selectedTeamId slot so the rest of the
  // state machinery (chip selection, URL, fetch keying) doesn't need a second
  // flag. It is serialized under its own URL param below.
  const teamId = favoritesActive
    ? "favorites"
    : searchParams.get("teamId") || null;
  const page = parseInt(searchParams.get("page") || "1", 10);
  const tagFilters = deserializeTagFilters(searchParams.get("filters"));
  const prospectId = searchParams.get("prospectId") || null;
  const section = parseSectionParam(searchParams.get("section"));

  // Sort lives in the URL alongside the filters and page so a shared or reloaded
  // link reproduces the table. Validated on the way in: an unrecognized key would
  // otherwise draw a sort arrow and hide drag-to-reorder while the server, which
  // ignores keys it does not know, returned its default order.
  const sort = parseTableSort(
    searchParams.get("sortBy"),
    searchParams.get("sortDir")
  );

  return {
    searchQuery: search,
    selectedTeamId: teamId,
    currentPage: page > 0 ? page : 1,
    tagFilters,
    prospectId,
    section,
    sort,
  };
};

export const PlayersSection = () => {
  const { effectiveOrganizationId, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Merge is irreversible but available to any authenticated user (matching the
  // API's plain [Authorize] guard). Routes are already auth-gated, so a
  // signed-in user is the only requirement.
  const canMerge = user !== null;

  // Initialize state from URL params
  const initialState = getInitialStateFromUrl(searchParams);

  const [prospects, setProspects] = useState<ProspectResponse[]>([]);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [teamChips, setTeamChips] = useState<TeamResponse[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(
    initialState.selectedTeamId
  );
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    initialState.searchQuery
  );
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [sort, setSort] = useState<TableSort>(initialState.sort);
  /**
   * The view the rows currently on screen were actually fetched for — as
   * distinct from the controls, which change the instant they are clicked.
   *
   * Anything that both (a) changes what the server returns and (b) is also
   * applied client-side to the rows has to live here, or the client-side half
   * runs against the previous page while the new one is still in flight. See
   * `displayedProspects`.
   */
  const [appliedView, setAppliedView] = useState<{
    sortColumn: ProspectSortColumn | null;
    isFavoritesTab: boolean;
  }>({
    sortColumn: initialState.sort.column,
    isFavoritesTab: initialState.selectedTeamId === "favorites",
  });
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Whether the first list request has settled. `loading` starts false, so on its own it would
  // tell the tour the page is ready before any rows were asked for.
  const [hasLoadedProspects, setHasLoadedProspects] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExportsPanelOpen, setIsExportsPanelOpen] = useState(false);
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(
    initialState.prospectId
  );
  const [isPanelOpen, setIsPanelOpen] = useState(!!initialState.prospectId);
  // One-shot deep-link param from Latest Notes (`?section=notes`). Consumed when
  // the viewer opens with the URL-seeded prospect; cleared on close or on any
  // subsequent prospect click so re-opens default to the Profile tab.
  const [pendingSection, setPendingSection] = useState<ProspectSection | null>(
    initialState.section
  );
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isAddTeamPanelOpen, setIsAddTeamPanelOpen] = useState(false);
  const [isManageTeamsPanelOpen, setIsManageTeamsPanelOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [tagFilters, setTagFilters] = useState<TagFilters>(
    initialState.tagFilters
  );
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);
  const [columnLayout, setColumnLayout] =
    useState<PlayersColumnLayout>(EMPTY_COLUMN_LAYOUT);
  const [gradYearOptions, setGradYearOptions] = useState<number[]>(() =>
    buildGradYearOptions()
  );
  const [tagDefinitions, setTagDefinitions] = useState<DefaultTagDefinition[]>(
    []
  );

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddToTeamModalOpen, setIsAddToTeamModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  // Reorder mode state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderProspectsList, setReorderProspectsList] = useState<ProspectResponse[]>([]);
  const [isLoadingReorder, setIsLoadingReorder] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Table tag modal state
  const [tableTagModalVisible, setTableTagModalVisible] = useState(false);
  const [activeTableProspectId, setActiveTableProspectId] = useState<
    string | null
  >(null);
  const [activeTableTagName, setActiveTableTagName] = useState<string | null>(
    null
  );

  // Track if component has mounted to prevent sync on initial render
  const hasMounted = useRef(false);
  // Track initial org ID to prevent reset on first mount
  const initialOrgId = useRef<string | null>(effectiveOrganizationId);
  // Track if team chips have been initialized from localStorage
  const teamChipsInitialized = useRef(false);
  // Track which org the current `teams` state belongs to (prevents stale rehydration)
  const teamsOrgId = useRef<string | null>(null);

  // Compute active table prospect and tag for modal
  const activeTableProspect = useMemo(() => {
    if (!activeTableProspectId) return null;
    return prospects.find((p) => p.id === activeTableProspectId) || null;
  }, [activeTableProspectId, prospects]);

  const activeTableTagDefinition = useMemo(() => {
    if (!activeTableTagName) return null;
    return (
      tagDefinitions.find((def) => def.tagName === activeTableTagName) || null
    );
  }, [activeTableTagName, tagDefinitions]);

  const activeTableTagValues = useMemo(() => {
    if (!activeTableProspect || !activeTableTagName) return [];
    return (
      activeTableProspect.defaultTags
        ?.filter((tag) => tag.tagName === activeTableTagName)
        .map((tag) => tag.tagValue) || []
    );
  }, [activeTableProspect, activeTableTagName]);

  // Sync state to URL params whenever filters change (after initial mount)
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const params = new URLSearchParams();

    if (debouncedSearchQuery) {
      params.set("search", debouncedSearchQuery);
    }

    if (selectedTeamId === "favorites") {
      params.set("favorites", "true");
    } else if (selectedTeamId) {
      params.set("teamId", selectedTeamId);
    }

    if (currentPage > 1) {
      params.set("page", currentPage.toString());
    }

    const serializedFilters = serializeTagFilters(tagFilters);
    if (serializedFilters) {
      params.set("filters", serializedFilters);
    }

    if (selectedProspectId) {
      params.set("prospectId", selectedProspectId);
    }

    if (sort.column) {
      params.set("sortBy", sort.column);
      params.set("sortDir", sort.direction);
    }

    setSearchParams(params, { replace: true });
  }, [
    debouncedSearchQuery,
    selectedTeamId,
    currentPage,
    tagFilters,
    selectedProspectId,
    sort,
    setSearchParams,
  ]);

  // Reset state when organization changes (but not on initial mount or when transitioning from null)
  useEffect(() => {
    // Skip reset if:
    // 1. This is the first time we're seeing a non-null org ID
    // 2. Org ID hasn't actually changed (same as before)
    if (
      initialOrgId.current === null ||
      effectiveOrganizationId === initialOrgId.current
    ) {
      // Update initialOrgId if this is the first non-null value
      if (initialOrgId.current === null && effectiveOrganizationId !== null) {
        initialOrgId.current = effectiveOrganizationId;
      }
      return;
    }

    // If we get here, the org actually changed (not just loaded for the first time)
    setProspects([]);
    setTeams([]);
    setTeamChips([]);
    setSelectedTeamId(null);
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setCurrentPage(1);
    setSort(DEFAULT_TABLE_SORT);
    setAppliedView({ sortColumn: null, isFavoritesTab: false });
    setTagFilters({});
    setSelectedProspectId(null);
    setIsPanelOpen(false);
    setPendingSection(null);
    // Drop any bulk selection — ids belong to the previous org and must not
    // be reused for actions taken after the switch.
    setSelectedIds(new Set());
    clearTeamChipIds(initialOrgId.current);
    teamsOrgId.current = null;
    // Reset initialization flag so new org loads from localStorage
    teamChipsInitialized.current = false;
    // Update to new org so switching back to a previous org still triggers a reset
    initialOrgId.current = effectiveOrganizationId;
  }, [effectiveOrganizationId]);

  // Fetch teams and tag definitions when org changes
  useEffect(() => {
    const fetchInitialData = async () => {
      const orgIdAtFetchTime = effectiveOrganizationId;
      try {
        const [teamsData, definitions, gradYears] = await Promise.all([
          getTeams(),
          getDefaultTagDefinitions(),
          // Non-fatal: a failure here must not block teams/tag definitions, and
          // the Grad Year filter still renders from the generated range fallback.
          getGradYears().catch((err) => {
            console.error("Failed to fetch grad years:", err);
            return [] as number[];
          }),
        ]);
        // Mark teams as fresh for this org before updating state so Effect 3
        // can guard against stale teams when it runs after this re-render
        teamsOrgId.current = orgIdAtFetchTime;
        setTeams(teamsData);
        setTagDefinitions(definitions);
        setGradYearOptions(buildGradYearOptions(gradYears));
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      }
    };
    fetchInitialData();
  }, [effectiveOrganizationId]);

  // Debounce search query
  useEffect(() => {
    // Skip debounce on first render since we already initialized debouncedSearchQuery from URL
    if (!hasMounted.current) {
      return;
    }

    const timer = setTimeout(() => {
      // Only update debounced query if it actually changed
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        setCurrentPage(1); // Reset to page 1 when search changes
        setSelectedIds(new Set()); // Clear selections when search changes
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery, debouncedSearchQuery]);

  // Load team chips from localStorage (runs once when teams load)
  useEffect(() => {
    if (teams.length === 0 || !effectiveOrganizationId) return;

    // Skip if already initialized for this org
    if (teamChipsInitialized.current) return;

    // Only run when teams are fresh for the current org. Without this guard,
    // this effect fires immediately on org change with the previous org's stale
    // teams state, prematurely sets teamChipsInitialized=true, and causes the
    // save effect to write old chip IDs into the new org's localStorage.
    if (teamsOrgId.current !== effectiveOrganizationId) return;

    // Load team chips from localStorage
    const storedIds = getStoredTeamChipIds(effectiveOrganizationId);
    const rehydrated = rehydrateTeamChips(storedIds, teams);
    setTeamChips(rehydrated);

    // Mark that we've initialized from localStorage
    teamChipsInitialized.current = true;

    // Handle teamId in URL that's not in chips (only on initial load)
    const urlTeamId = initialState.selectedTeamId;
    if (urlTeamId) {
      const isInChips = rehydrated.some((chip) => chip.id === urlTeamId);

      if (!isInChips) {
        // SECURITY: Only add team if it exists in user's organization
        // The 'teams' array is already filtered by organization from the API
        const teamToAdd = teams.find((t) => t.id === urlTeamId);

        if (teamToAdd) {
          // Team exists in user's org, add it to chips
          setTeamChips((prev) => [...prev, teamToAdd]);
        } else {
          // Team doesn't exist in user's org (or doesn't exist at all)
          // Clear selection to prevent unauthorized access
          console.warn(
            `Team ${urlTeamId} not found in organization, clearing selection`
          );
          setSelectedTeamId(null);
        }
      }
    }
  }, [teams, effectiveOrganizationId]);

  // Sync teamChips to localStorage whenever they change
  useEffect(() => {
    // Don't sync until after we've initialized from localStorage
    if (!teamChipsInitialized.current || !effectiveOrganizationId) return;

    const teamIds = teamChips.map((t) => t.id);
    saveTeamChipIds(effectiveOrganizationId, teamIds);
  }, [teamChips, effectiveOrganizationId]);

  /*
    Seed the page size from the organization's stored preference.
    Keyed by org and re-run on org change, so switching organizations picks up
    that org's size rather than carrying the previous one over. Reset to the
    shared default when an org has nothing stored, which is what
    getStoredPageSize returns.
  */
  useEffect(() => {
    if (!effectiveOrganizationId) return;
    setPageSize(getStoredPageSize(effectiveOrganizationId));
  }, [effectiveOrganizationId]);

  // Same pattern for the column layout: keyed by org, re-read on org change.
  useEffect(() => {
    if (!effectiveOrganizationId) return;
    setColumnLayout(getStoredColumnLayout(effectiveOrganizationId));
  }, [effectiveOrganizationId]);

  // The query describing the table as currently displayed. Shared by the main
  // fetch and every post-mutation refetch below, so a refresh after a delete or
  // a bulk add cannot silently drop the sort or a filter.
  const prospectQuery = useMemo(() => {
    const isFavoritesTab = selectedTeamId === "favorites";
    return {
      pageNumber: currentPage,
      pageSize,
      teamId: isFavoritesTab ? undefined : selectedTeamId || undefined,
      isFavorite: isFavoritesTab ? true : undefined,
      searchTerm: debouncedSearchQuery || undefined,
      tagFilters: Object.keys(tagFilters).length > 0 ? tagFilters : undefined,
      sortBy: sort.column ?? undefined,
      sortDirection: sort.column ? sort.direction : undefined,
    };
  }, [
    currentPage,
    pageSize,
    selectedTeamId,
    debouncedSearchQuery,
    tagFilters,
    sort,
  ]);

  // Monotonic id for list requests. Responses that are no longer the newest are
  // discarded: several handlers fire a silent refetch after an await, so a slow
  // one issued *before* a sort click can otherwise resolve after it and overwrite
  // the sorted rows with unsorted ones — with no spinner and no error, leaving a
  // table that contradicts its own header until the next interaction.
  const listRequestSeq = useRef(0);

  // Fetch prospects from server with current filters. Pass silent=true to skip
  // the loading spinner — used by post-mutation refreshes that pick up changes
  // propagated to linked-prospect siblings without flashing the table.
  const fetchProspects = useCallback(async (silent = false) => {
    const seq = ++listRequestSeq.current;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await getProspects(prospectQuery);
      if (seq !== listRequestSeq.current) return;
      setProspects(response.items);
      // Batched with the rows, and read from the query that produced *this*
      // response, so the client-side view logic can never run against a page it
      // does not describe. Safe to leave alone in the handlers that mutate
      // `prospects` without refetching: those all map or filter the same page in
      // place, so the view that produced it is still accurate.
      setAppliedView({
        sortColumn: prospectQuery.sortBy ?? null,
        isFavoritesTab: prospectQuery.isFavorite === true,
      });
      setTotalCount(response.totalCount);
      return response;
    } catch (err) {
      if (seq !== listRequestSeq.current) return;
      const errorMessage = getSafeErrorMessage(err, "Failed to load prospects");
      setError(errorMessage);
      console.error("Failed to load prospects:", err);
    } finally {
      // `silent` gates *raising* the spinner, never lowering it. Whoever is
      // newest when it settles knows nothing is in flight, so it has to put the
      // spinner down even if it never put it up — otherwise a silent refetch
      // that supersedes a loud one strands `loading` at true with nothing left
      // to clear it, and the table sits there dimmed and inert.
      if (seq === listRequestSeq.current) {
        setLoading(false);
        setHasLoadedProspects(true);
      }
    }
  }, [prospectQuery]);

  /**
   * Always the newest `fetchProspects`.
   *
   * Handlers that refetch *after* an await hold whichever callback existed when
   * they started, and that callback closes over the `prospectQuery` of its own
   * render. If the user sorts during the await, the stale callback then issues a
   * request carrying the *old* query — and because it is issued later, it wins
   * the sequence guard and overwrites the correctly sorted rows. Nothing
   * re-requests them afterwards, so the table contradicts its own header until
   * the user touches something else. Reading through this ref keeps every
   * post-await refetch on the current query.
   */
  const fetchProspectsRef = useRef(fetchProspects);
  useEffect(() => {
    fetchProspectsRef.current = fetchProspects;
  }, [fetchProspects]);

  /**
   * Refetch the current page after rows may have been removed, then clamp the
   * page if it no longer exists — deleting the last row on the last page has to
   * fall back a page rather than show an empty table.
   */
  const refetchAndClampPage = useCallback(async () => {
    const response = await fetchProspectsRef.current();
    if (!response) return;

    const maxValidPage = Math.max(1, Math.ceil(response.totalCount / pageSize));
    if (currentPage > maxValidPage) {
      setCurrentPage(maxValidPage);
    }
    // Reads the fetch through a ref, so it is deliberately not a dependency.
  }, [currentPage, pageSize]);

  // Fetch prospects whenever filters or page changes
  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  // Reset to page 1 when team filter changes
  const handleTeamChange = (teamId: string | null) => {
    setSelectedTeamId(teamId);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  // Add a team to the chips row (idempotent — never push a duplicate id, which
  // would collide with the sortable list's keys).
  const handleAddTeamChip = (team: TeamResponse) => {
    setTeamChips((prev) =>
      prev.some((t) => t.id === team.id) ? prev : [...prev, team]
    );
  };

  // Remove a team from the chips row
  const handleRemoveTeamChip = (teamId: string) => {
    setTeamChips((prev) => prev.filter((t) => t.id !== teamId));
    // If the removed team was selected, switch to "All Teams"
    if (selectedTeamId === teamId) {
      setSelectedTeamId(null);
      setCurrentPage(1);
    }
  };

  // Close every team chip at once and land on "All Teams". One state update,
  // so the sync effect below writes the emptied list to localStorage once.
  // Team data is untouched: this only changes which tabs are open.
  const handleCloseAllTeamChips = () => {
    setTeamChips([]);
    if (selectedTeamId !== null) {
      handleTeamChange(null);
    }
  };

  // Delete a team permanently
  const handleDeleteTeam = async (teamId: string) => {
    try {
      await deleteTeam(teamId);

      // Remove from chips
      setTeamChips((prev) => prev.filter((t) => t.id !== teamId));

      // If the deleted team was selected, switch to "All Teams"
      if (selectedTeamId === teamId) {
        setSelectedTeamId(null);
        setCurrentPage(1);
      }

      // Refresh both prospects and teams lists
      const [, teamsData] = await Promise.all([
        fetchProspectsRef.current(),
        getTeams(),
      ]);
      setTeams(teamsData);
    } catch (err) {
      const errorMessage = getSafeErrorMessage(err, "Failed to delete team");
      console.error("Failed to delete team:", err);
      setError(errorMessage);
    }
  };

  // Handle team update (after rename or add player)
  const handleTeamUpdate = async () => {
    try {
      // Refresh both prospects and teams lists
      const [, teamsData] = await Promise.all([
        fetchProspectsRef.current(),
        getTeams(),
      ]);
      setTeams(teamsData);

      // Update team chips with new data (in case team was renamed)
      setTeamChips((prev) =>
        prev.map((chip) => {
          const updated = teamsData.find((t) => t.id === chip.id);
          return updated || chip;
        })
      );
    } catch (err) {
      const errorMessage = getSafeErrorMessage(err, "Failed to refresh data");
      console.error("Failed to refresh data after team update:", err);
      setError(errorMessage);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    // Note: page reset is handled in the debounce effect
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  /*
    Changing the page size re-slices the whole list, so the current page number
    is meaningless afterwards — page 8 of 15-row pages does not exist once there
    are 100 rows per page. Back to page 1, the same reset every other filter
    change performs.
  */
  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    savePageSize(effectiveOrganizationId, nextPageSize);
    setCurrentPage(1);
  };

  const handleColumnLayoutChange = (nextLayout: PlayersColumnLayout) => {
    setColumnLayout(nextLayout);
    saveColumnLayout(effectiveOrganizationId, nextLayout);

    /*
      Hiding the column the table is sorted by has to clear the sort. Sort state
      lives in the URL and is re-parsed on every read, so leaving it would keep
      the rows genuinely sorted by a column that now has no header and no arrow
      — sorted, with nothing on screen saying so, and no way to undo it short of
      editing the URL.
    */
    if (isSortedColumnHidden(sort.column, tagDefinitions, nextLayout)) {
      setSort(DEFAULT_TABLE_SORT);
      setCurrentPage(1);
    }
  };

  /**
   * The rows as displayed.
   *
   * The server already returns favorites first, so `pinFavoritesFirst` mostly
   * re-states the server's order — but it is what makes the optimistic favorite
   * toggle move a row to the top without a refetch, so it has to stay. It must
   * NOT run when a column sort is active: it would re-order the favorites block
   * by `favoritedAt` and undo the server's ordering of those rows by the chosen
   * column, leaving the table contradicting its own sort arrow.
   *
   * Both the favourites filter and the pin decision are keyed off `appliedView`
   * — what the visible rows were fetched for — rather than the live controls,
   * which flip the moment they are clicked. Reading the live values here applies
   * them to the *previous* page: clicking a header would un-pin the stale rows
   * so they jump once on click and again when the data lands, and clicking the
   * Favorites chip would filter a page that may contain no favorites at all,
   * emptying the table and collapsing every column to its header width.
   */
  const displayedProspects = useMemo(() => {
    const rows = appliedView.isFavoritesTab
      ? prospects.filter((p) => p.isFavorite)
      : prospects;
    return appliedView.sortColumn ? rows : pinFavoritesFirst(rows);
  }, [prospects, appliedView]);

  /**
   * Which team chips carry a JUCO marker, rolled up from the loaded players.
   *
   * Rolled up rather than looked up because a team has no reachable school
   * division — see `utils/juco-team-rollup` for the threshold and why. Read off
   * the full `prospects` page, not `displayedProspects`, so a team's marker does
   * not flicker when the coach pins favourites or sorts a column.
   */
  const jucoTeamIdSet = useMemo(() => jucoTeamIds(prospects), [prospects]);

  // Drag-to-reorder saves the team's manual order, so it is only offered on the
  // unmodified roster view. A column sort is one more way the displayed order
  // stops matching the order that would be written, alongside the existing
  // search and filter gates.
  const canReorder =
    !!selectedTeamId &&
    selectedTeamId !== "favorites" &&
    !debouncedSearchQuery &&
    countActiveFilters(tagFilters) === 0 &&
    !sort.column;

  // Sorting reorders the whole result set server-side, so the row that was on
  // page 3 can land on page 1. Staying on the old page would show an arbitrary
  // slice of the new order, and a selection carried across it would no longer
  // match what the user can see.
  const handleSortChange = (column: ProspectSortColumn) => {
    setSort((current) => nextTableSort(current, column));
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleProspectClick = (
    prospectId: string,
    section?: ProspectSection
  ) => {
    setSelectedProspectId(prospectId);
    setIsPanelOpen(true);
    // Fresh click from the table — consume any lingering deep-link section,
    // unless this click asked for a specific one (e.g. the video count badge).
    setPendingSection(section ?? null);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setSelectedProspectId(null);
    setPendingSection(null);
    // Silent refetch so the row's badge counts reflect anything the coach did
    // inside the panel. Uploading or deleting videos in the Videos tab has no
    // other path back to the table, so without this a prospect whose clips were
    // just deleted keeps showing the old count. Preserves page and filters.
    void fetchProspects(true);
  };

  const handleSelectionChange = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleBulkDeleteConfirm = async () => {
    setError(null);
    try {
      await bulkDeleteProspects([...selectedIds]);
      setSelectedIds(new Set());
      setIsDeleteModalOpen(false);

      await refetchAndClampPage();
    } catch (err) {
      const errorMessage = getSafeErrorMessage(err, "Failed to delete players");
      setError(errorMessage);
      console.error("Failed to bulk delete prospects:", err);
    }
  };

  const handleOpenAddToTeam = () => {
    if (selectedIds.size === 0) return;
    setIsAddToTeamModalOpen(true);
  };

  // Full prospect records for the current selection — the merge modal needs the
  // scalar/about/metrics data + `updatedAt` (for expectedVersions) that only the
  // loaded list rows carry.
  const selectedProspects = useMemo(
    () => prospects.filter((p) => selectedIds.has(p.id)),
    [prospects, selectedIds]
  );

  const handleOpenMerge = () => {
    if (!canMerge || selectedIds.size < 2) return;
    setIsMergeModalOpen(true);
  };

  // After a successful merge: drop the retired losers from the cached list and
  // open the survivor. No optimistic removal happens before this point — on any
  // error the list stays exactly as-is.
  const handleMergeSuccess = (survivorId: string, mergedIds: string[]) => {
    const retired = new Set(mergedIds);
    setIsMergeModalOpen(false);
    setSelectedIds(new Set());
    setProspects((prev) => prev.filter((p) => !retired.has(p.id)));
    setTotalCount((prev) => Math.max(0, prev - mergedIds.length));
    // Open the survivor so the coach lands on the combined profile, and silently
    // refresh so reparented children / sibling propagation are reflected.
    handleProspectClick(survivorId);
    void fetchProspectsRef.current(true);
  };

  // STALE_READ / ALREADY_MERGED / PROSPECT_NOT_FOUND — the local snapshot is
  // stale. Re-fetch so the next retry carries fresh `updatedAt` values. We keep
  // the modal open (it shows the error) and do NOT remove any rows.
  const handleMergeStaleData = () => {
    void fetchProspectsRef.current(true);
  };

  const handleAddToTeamSuccess = async (
    _result: BulkAddProspectsToTeamResponse,
    teamCreated: boolean
  ) => {
    setError(null);
    // Drop the selection immediately so the modal's summary view reflects a
    // clean state when the user dismisses it.
    setSelectedIds(new Set());

    // Refresh prospects for the current view so team columns update. Bulk add
    // never removes rows, so no page-clamping needed (unlike handleBulkDeleteConfirm).
    await fetchProspectsRef.current();

    // If a brand-new team was created, refresh the teams list so chips/typeahead
    // pick it up without requiring a page reload.
    if (teamCreated) {
      try {
        const freshTeams = await getTeams();
        setTeams(freshTeams);
      } catch (err) {
        console.error("Failed to refresh teams after bulk add:", err);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleProspectDeleted = async (_prospectId: string) => {
    // Close the panel immediately for responsive UI
    // Note: prospectId parameter is required by callback signature but unused
    // because we refetch all data from the server instead of filtering locally
    setIsPanelOpen(false);
    setSelectedProspectId(null);

    // Refetch the current page rather than filtering locally, so the row is
    // backfilled from the next page and the count stays accurate.
    await refetchAndClampPage();
  };

  const handleFavoriteToggle = async (
    prospectId: string,
    currentIsFavorite: boolean
  ) => {
    const previousFavoritedAt =
      prospects.find((p) => p.id === prospectId)?.favoritedAt ?? null;
    const optimisticFavoritedAt = currentIsFavorite
      ? null
      : new Date().toISOString();

    setProspects((prevProspects) =>
      prevProspects.map((p) =>
        p.id === prospectId
          ? {
              ...p,
              isFavorite: !currentIsFavorite,
              favoritedAt: optimisticFavoritedAt,
            }
          : p
      )
    );

    try {
      const response = await toggleFavorite(prospectId, currentIsFavorite);
      // Reconcile optimistic timestamp with the server-authoritative one.
      setProspects((prevProspects) =>
        prevProspects.map((p) =>
          p.id === prospectId
            ? { ...p, favoritedAt: response.favoritedAt }
            : p
        )
      );
      // Silently re-fetch so any linked-prospect siblings (matching email/phone)
      // updated server-side reflect their new favorite state in the list too.
      void fetchProspectsRef.current(true);
    } catch (err) {
      setProspects((prevProspects) =>
        prevProspects.map((p) =>
          p.id === prospectId
            ? {
                ...p,
                isFavorite: currentIsFavorite,
                favoritedAt: previousFavoritedAt,
              }
            : p
        )
      );

      const errorMessage = getSafeErrorMessage(
        err,
        "Failed to update favorite status"
      );
      setError(errorMessage);
      console.error("Failed to toggle favorite:", err);
    }
  };

  const handleTagUpdate = async (
    prospectId: string,
    tagName: string,
    values: string[]
  ) => {
    try {
      const updatedTags = await updateProspectTags(prospectId, tagName, values);

      // Merge the updated tags with existing tags (remove old tags for this tagName, add new ones)
      setProspects((prevProspects) =>
        prevProspects.map((p) => {
          if (p.id === prospectId) {
            const otherTags =
              p.defaultTags?.filter((t) => t.tagName !== tagName) || [];
            return { ...p, defaultTags: [...otherTags, ...updatedTags] };
          }
          return p;
        })
      );
      // Silently re-fetch so linked-prospect siblings updated server-side show
      // their new tag values in the table without a manual reload.
      void fetchProspectsRef.current(true);
    } catch (err) {
      console.error("Failed to update tag:", err);
      throw err; // Re-throw for ProspectViewerPanel to handle
    }
  };

  const handlePositionPlayerMetricUpdate = async (
    prospectId: string,
    field: string,
    value: number | null
  ) => {
    try {
      const updatedProspect = await updatePositionPlayerMetric(prospectId, {
        [field]: value,
      });

      // Optimistically update the prospects array with the updated prospect
      setProspects((prevProspects) =>
        prevProspects.map((p) => (p.id === prospectId ? updatedProspect : p))
      );
      void fetchProspectsRef.current(true);
    } catch (err) {
      console.error("Failed to update position player metric:", err);
      throw err;
    }
  };

  const handlePitcherMetricUpdate = async (
    prospectId: string,
    field: string,
    value: number | null
  ) => {
    try {
      const updatedProspect = await updatePitcherMetric(prospectId, {
        [field]: value,
      });

      // Optimistically update the prospects array with the updated prospect
      setProspects((prevProspects) =>
        prevProspects.map((p) => (p.id === prospectId ? updatedProspect : p))
      );
      void fetchProspectsRef.current(true);
    } catch (err) {
      console.error("Failed to update pitcher metric:", err);
      throw err;
    }
  };

  const handleProfileSave = async (
    prospectId: string,
    data: UpdateProspectProfileData
  ): Promise<ProspectResponse> => {
    try {
      const updatedProspect = await updateProspectProfile(prospectId, data);

      // Optimistically update the prospects array with the updated prospect
      setProspects((prevProspects) =>
        prevProspects.map((p) => (p.id === prospectId ? updatedProspect : p))
      );
      // A profile save can change email/phone — silently re-fetch so any newly-
      // linked or unlinked sibling rows update in the table.
      void fetchProspectsRef.current(true);

      return updatedProspect;
    } catch (err) {
      console.error("Failed to update prospect profile:", err);
      throw err;
    }
  };

  const handleJerseyNumberUpdate = async (
    teamId: string,
    prospectId: string,
    jerseyNumber: number | null
  ): Promise<void> => {
    // Store the previous jersey number for rollback
    let previousJerseyNumber: number | null = null;

    // Optimistic update: immediately update the jersey number in the local state
    setProspects((prevProspects) =>
      prevProspects.map((p) => {
        if (p.id === prospectId) {
          const updatedTeams = p.teams.map((team) => {
            if (team.id === teamId) {
              previousJerseyNumber = team.jerseyNumber ?? null;
              return { ...team, jerseyNumber };
            }
            return team;
          });
          return { ...p, teams: updatedTeams };
        }
        return p;
      })
    );

    try {
      // API call in background
      await updateProspectTeam(teamId, prospectId, { jerseyNumber });
    } catch (err) {
      // Rollback on error: restore the previous jersey number
      setProspects((prevProspects) =>
        prevProspects.map((p) => {
          if (p.id === prospectId) {
            const revertedTeams = p.teams.map((team) => {
              if (team.id === teamId) {
                return { ...team, jerseyNumber: previousJerseyNumber };
              }
              return team;
            });
            return { ...p, teams: revertedTeams };
          }
          return p;
        })
      );
      console.error("Failed to update jersey number:", err);
      throw err;
    }
  };

  const handleAddProspect = () => {
    setIsAddPanelOpen(true);
  };

  const handleAddPanelClose = () => {
    setIsAddPanelOpen(false);
  };

  const handleAddProspectSuccess = async (prospect: ProspectResponse) => {
    const addedTeam = prospect.teams[0];
    if (!addedTeam) {
      fetchProspectsRef.current();
      return;
    }

    try {
      const teamsData = await getTeams();
      setTeams(teamsData);

      const teamToSelect = teamsData.find((t) => t.id === addedTeam.id);
      if (teamToSelect) {
        setTeamChips((prev) =>
          prev.some((c) => c.id === teamToSelect.id)
            ? prev.map((c) => (c.id === teamToSelect.id ? teamToSelect : c))
            : [...prev, teamToSelect]
        );
      }

      setSelectedTeamId(addedTeam.id);
      setCurrentPage(1);
    } catch (err) {
      console.error("Failed to refresh teams after add prospect:", err);
      fetchProspectsRef.current();
    }
  };

  const handleAddTeam = () => {
    setIsAddTeamPanelOpen(true);
  };

  const handleAddTeamPanelClose = () => {
    setIsAddTeamPanelOpen(false);
  };

  const handleAddTeamSuccess = (team: TeamResponse) => {
    setTeams((prev) => [...prev, team]);
    setTeamChips((prev) => [...prev, team]);
    setSelectedTeamId(team.id);
    setCurrentPage(1);
  };

  // Refresh after a bulk delete or restore. Deleted teams won't be in the fresh list, so
  // chips referencing them are dropped; and because a bulk action moves players on and off
  // rosters, the prospect list is refetched alongside the team list — not just the chips row.
  const handleTeamsChanged = async () => {
    try {
      const [, teamsData] = await Promise.all([fetchProspects(), getTeams()]);
      setTeams(teamsData);

      const stillExists = new Set(teamsData.map((t) => t.id));
      setTeamChips((prev) => prev.filter((chip) => stillExists.has(chip.id)));

      // If the selected team no longer exists, fall back to "All Teams".
      if (selectedTeamId && selectedTeamId !== "favorites" && !stillExists.has(selectedTeamId)) {
        setSelectedTeamId(null);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error("Failed to refresh teams after bulk team change:", err);
    }
  };

  const handleFilterApply = (filters: TagFilters) => {
    // The modal already prunes; this is the second line of defence, so no code path can
    // put a JC Year into the URL without the JUCO level that makes it matchable.
    setTagFilters(pruneJucoYearFacet(filters));
    setIsFilterModalOpen(false);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleFilterReset = () => {
    setTagFilters({});
    setIsFilterModalOpen(false);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleEnterReorderMode = async () => {
    if (!selectedTeamId) return;
    setIsLoadingReorder(true);
    setError(null);
    try {
      const all = await getAllProspectsForTeam(selectedTeamId);
      setReorderProspectsList(all);
      setIsReorderMode(true);
      // Reorder hides the checkboxes, so a surviving selection would be invisible
      // and confusing if the user exits without saving.
      setSelectedIds(new Set());
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to load prospects for reorder"));
    } finally {
      setIsLoadingReorder(false);
    }
  };

  const handleReorderChange = (newOrder: ProspectResponse[]) => {
    setReorderProspectsList(newOrder);
  };

  const handleSaveOrder = async () => {
    if (!selectedTeamId) return;
    setIsSavingOrder(true);
    setError(null);
    try {
      await reorderProspects(selectedTeamId, reorderProspectsList.map((p) => p.id));
      setIsReorderMode(false);
      setReorderProspectsList([]);
      fetchProspectsRef.current();
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to save order"));
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleCancelReorder = () => {
    setIsReorderMode(false);
    setReorderProspectsList([]);
  };

  const handleFilterButtonClick = () => {
    setIsFilterModalOpen(true);
  };

  // Both halves of exporting live behind one toolbar button now. It opens the panel; the
  // panel is where an export is started and where the finished ones are collected. Two
  // buttons a letter apart gave no clue which did which.
  const exportFilterSummary = useMemo(() => {
    const parts: string[] = [];
    const team =
      selectedTeamId && selectedTeamId !== "favorites"
        ? teams.find((t) => t.id === selectedTeamId)
        : undefined;
    if (team) parts.push(team.name);
    if (debouncedSearchQuery) parts.push(`“${debouncedSearchQuery}”`);
    return parts.length > 0 ? parts.join(" · ") : "All players";
  }, [selectedTeamId, teams, debouncedSearchQuery]);

  // Throws on failure so the panel can report it next to the button that was pressed,
  // rather than behind the section-level error banner the panel covers.
  const handleCreateExport = useCallback(async () => {
    // Note: the export ignores the favorites sentinel — the endpoint doesn't accept an
    // isFavorite param yet. Exporting from the favorites chip covers all matching
    // prospects (sans favorite filter).
    await requestProspectExport({
      teamId: selectedTeamId === "favorites" ? undefined : selectedTeamId || undefined,
      searchTerm: debouncedSearchQuery || undefined,
      tagFilters: Object.keys(tagFilters).length > 0 ? tagFilters : undefined,
    });
  }, [selectedTeamId, debouncedSearchQuery, tagFilters]);

  const handleUploadRoster = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleUploadSuccess = async (createdTeamIds: string[] = []) => {
    setIsUploadModalOpen(false);

    // Refresh teams first so we can resolve newly created team IDs to full
    // TeamResponse objects before deciding what to add to chips / select.
    try {
      const teamsData = await getTeams();
      setTeams(teamsData);

      if (createdTeamIds.length > 0) {
        const dedupedIds = Array.from(new Set(createdTeamIds));
        const teamsToOpen = dedupedIds
          .map((id) => teamsData.find((t) => t.id === id))
          .filter((t): t is TeamResponse => Boolean(t));

        if (teamsToOpen.length > 0) {
          setTeamChips((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const additions = teamsToOpen.filter((t) => !existingIds.has(t.id));
            return additions.length > 0 ? [...prev, ...additions] : prev;
          });

          // Focus the most recently created team — for a multi-team import this
          // is the last team in the run; for single-team it is the only team.
          const lastTeamId = teamsToOpen[teamsToOpen.length - 1].id;
          setSelectedTeamId(lastTeamId);
          setCurrentPage(1);
          // Skip the generic fetchProspects below — switching selectedTeamId
          // already triggers a refetch via the filter effect.
          return;
        }
      }

      // No new teams to focus — just refresh the current view.
      await fetchProspectsRef.current();
    } catch (err) {
      console.error("Failed to refresh data after upload:", err);
    }
  };

  const handleTableTagClick = useCallback(
    (prospectId: string, tagName: string) => {
      setActiveTableProspectId(prospectId);
      setActiveTableTagName(tagName);
      setTableTagModalVisible(true);
    },
    []
  );

  const handleTableTagSave = useCallback(
    async (values: string[]) => {
      if (!activeTableProspectId || !activeTableTagName) return;

      try {
        await handleTagUpdate(
          activeTableProspectId,
          activeTableTagName,
          values
        );
        setTableTagModalVisible(false);
        setActiveTableProspectId(null);
        setActiveTableTagName(null);
      } catch (error) {
        console.error("Failed to update tag from table:", error);
        // Error handling already in handleTagUpdate
      }
    },
    [activeTableProspectId, activeTableTagName, handleTagUpdate]
  );

  const handleTableTagModalClose = useCallback(() => {
    setTableTagModalVisible(false);
    setActiveTableProspectId(null);
    setActiveTableTagName(null);
  }, []);

  useTour("players", {
    ready: hasLoadedProspects && !loading,
    hasData: displayedProspects.length > 0,
  });

  return (
    <ErrorBoundary
      fallbackTitle="Players Section Error"
      fallbackMessage="We encountered an error loading the players section. This may be temporary."
      onReset={() => {
        // Optional: Reset any local state or refetch data when user clicks "Try Again"
        console.log("Players section error boundary reset");
      }}
    >
      <div className="players-section">
        {/*
          The Get Started checklist a new program works through. It decides for itself whether to
          render anything at all — nothing here gates it — and returns null for an established org,
          a user who dismissed it, or an API that predates the endpoint.
        */}
        <GetStartedPanel />

        <PlayersToolbar
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onAddProspect={handleAddProspect}
          onAddTeam={handleAddTeam}
          onManageTeams={() => setIsManageTeamsPanelOpen(true)}
          onExport={() => setIsExportsPanelOpen(true)}
          onUploadRoster={handleUploadRoster}
          onFilterClick={handleFilterButtonClick}
          activeFilterCount={countActiveFilters(tagFilters)}
          tagDefinitions={tagDefinitions}
          columnLayout={columnLayout}
          onColumnLayoutChange={handleColumnLayoutChange}
          selectedCount={selectedIds.size}
          onDeleteSelected={() => {
            if (selectedIds.size > 500) {
              setError("Cannot delete more than 500 players at once. Please reduce your selection and try again.");
              return;
            }
            setIsDeleteModalOpen(true);
          }}
          onAddToTeam={() => {
            if (selectedIds.size > 500) {
              setError("Cannot add more than 500 players at once. Please reduce your selection and try again.");
              return;
            }
            handleOpenAddToTeam();
          }}
          onClearSelection={() => setSelectedIds(new Set())}
          canMerge={canMerge}
          onMerge={handleOpenMerge}
          isReorderMode={isReorderMode}
          canReorder={canReorder}
          isLoadingReorder={isLoadingReorder}
          isSavingOrder={isSavingOrder}
          onEnterReorderMode={handleEnterReorderMode}
          onSaveOrder={handleSaveOrder}
          onCancelReorder={handleCancelReorder}
        />

        <TeamChips
          teams={teams}
          selectedTeamId={selectedTeamId}
          teamChips={teamChips}
          onChange={handleTeamChange}
          onAddTeam={handleAddTeamChip}
          onRemoveTeam={handleRemoveTeamChip}
          onCloseAllTeams={handleCloseAllTeamChips}
          onDeleteTeam={handleDeleteTeam}
          onTeamUpdate={handleTeamUpdate}
          onReorderTeams={setTeamChips}
          onManageTeams={() => setIsManageTeamsPanelOpen(true)}
          jucoTeamIds={jucoTeamIdSet}
        />

        {error && <div className="players-section__error">{error}</div>}

        <PlayersTable
          prospects={isReorderMode ? reorderProspectsList : displayedProspects}
          tagDefinitions={tagDefinitions}
          loading={loading || isLoadingReorder}
          onProspectClick={handleProspectClick}
          onFavoriteToggle={handleFavoriteToggle}
          onTagClick={handleTableTagClick}
          selectedIds={selectedIds}
          onSelectionChange={handleSelectionChange}
          isReorderMode={isReorderMode}
          onReorder={handleReorderChange}
          sort={sort}
          onSortChange={handleSortChange}
          columnLayout={columnLayout}
        />

        {/*
          Deliberately not gated on `loading`. Unmounting the pager during a
          refetch is the same flicker as collapsing the table: everything below
          jumps up and back on every sort or page click — and it happens right
          under the cursor, so a second click lands on whatever moved into the
          gap. It stays mounted with the previous page's numbers, alongside the
          dimmed rows it describes. First load has totalCount 0, so it is absent
          until there is something to page through.
        */}
        {!isReorderMode && totalCount > 0 && (
          <TablePagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={handlePageSizeChange}
          />
        )}

        <ProspectViewerPanel
          prospectId={selectedProspectId}
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          initialSection={pendingSection}
          onFavoriteToggle={handleFavoriteToggle}
          onTagUpdate={handleTagUpdate}
          onPositionPlayerMetricUpdate={handlePositionPlayerMetricUpdate}
          onPitcherMetricUpdate={handlePitcherMetricUpdate}
          onProfileSave={handleProfileSave}
          onJerseyNumberUpdate={handleJerseyNumberUpdate}
          onProspectDeleted={handleProspectDeleted}
        />

        <TagSelectorModal
          visible={tableTagModalVisible}
          definition={activeTableTagDefinition}
          selectedValues={activeTableTagValues}
          onSave={handleTableTagSave}
          onClose={handleTableTagModalClose}
        />

        <AddProspectPanel
          isOpen={isAddPanelOpen}
          onClose={handleAddPanelClose}
          onSuccess={handleAddProspectSuccess}
        />

        <AddTeamPanel
          isOpen={isAddTeamPanelOpen}
          onClose={handleAddTeamPanelClose}
          onSuccess={handleAddTeamSuccess}
        />

        <ManageTeamsPanel
          isOpen={isManageTeamsPanelOpen}
          onClose={() => setIsManageTeamsPanelOpen(false)}
          onTeamsChanged={handleTeamsChanged}
        />

        <ProspectExportsPanel
          isOpen={isExportsPanelOpen}
          onClose={() => setIsExportsPanelOpen(false)}
          filterSummary={exportFilterSummary}
          onCreateExport={handleCreateExport}
        />

        <RosterUploadModal
          isOpen={isUploadModalOpen}
          onClose={handleUploadModalClose}
          onSuccess={handleUploadSuccess}
        />

        <TagFilterModal
          isOpen={isFilterModalOpen}
          definitions={tagDefinitions}
          gradYearOptions={gradYearOptions}
          currentFilters={tagFilters}
          onApply={handleFilterApply}
          onReset={handleFilterReset}
          onClose={() => setIsFilterModalOpen(false)}
          onManageTags={() => {
            setIsFilterModalOpen(false);
            setIsTagManagerOpen(true);
          }}
        />

        <TagDefinitionManagerModal
          isOpen={isTagManagerOpen}
          onClose={() => setIsTagManagerOpen(false)}
          onDefinitionsChanged={(updated) => setTagDefinitions(updated)}
        />

        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleBulkDeleteConfirm}
          title="Delete Players"
          message={`Are you sure you want to delete ${selectedIds.size} player${selectedIds.size !== 1 ? "s" : ""}? This cannot be undone.`}
          confirmText="Delete"
        />

        <AddPlayersToTeamModal
          isOpen={isAddToTeamModalOpen}
          prospectIds={[...selectedIds]}
          onClose={() => setIsAddToTeamModalOpen(false)}
          onSuccess={handleAddToTeamSuccess}
        />

        <MergeProspectsModal
          isOpen={isMergeModalOpen}
          prospects={selectedProspects}
          onClose={() => setIsMergeModalOpen(false)}
          onSuccess={handleMergeSuccess}
          onStaleData={handleMergeStaleData}
        />
      </div>
    </ErrorBoundary>
  );
};
